import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { Plus, LayoutTemplate, Copy, Trash2, Loader2, Search, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { useLayouts, useCloneLayout, useDeleteLayout } from '../api/hooks'
import { LayoutPreview } from '../components/layout-creator/LayoutPreview'
import type { Layout } from '../api/types'
import type { CanvasSlot } from '../components/editor-shared/canvasTypes'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeDate(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 2) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const CONTEXT_BAR: Record<string, string> = {
  official: 'bg-violet-400',
  book:     'bg-cyan-400',
}

// ─── Left rail: layout row ─────────────────────────────────────────────────────

function LayoutRow({
  layout,
  selected,
  onClick,
}: {
  layout: Layout
  selected: boolean
  onClick: () => void
}) {
  const barClass = CONTEXT_BAR[layout.usageContext[0]] ?? 'bg-fg-secondary'
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={layout.name}
      className={`relative w-full overflow-hidden text-left transition-colors ${
        selected ? 'bg-fill' : 'hover:bg-fill/60'
      }`}
    >
      <div className={`absolute inset-y-0 left-0 w-[3px] ${selected ? barClass : 'bg-transparent'}`} aria-hidden="true" />
      <div className="py-2.5 pl-5 pr-4">
        <div className="flex items-start justify-between gap-2">
          <span className={`truncate text-[12px] font-semibold leading-snug ${selected ? 'text-fg' : 'text-fg-secondary'}`}>
            {layout.name}
          </span>
          <span className="mt-px shrink-0 text-[10px] text-fg-secondary opacity-40">{relativeDate(layout.updatedAt)}</span>
        </div>
        {layout.description && (
          <p className="mt-0.5 truncate text-[11px] text-fg-secondary opacity-50">{layout.description}</p>
        )}
      </div>
    </button>
  )
}

// ─── Left rail: section header ─────────────────────────────────────────────────

function RailSection({ label, count, children }: { label: string; count: number; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between px-4 pb-1 pt-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary opacity-40">{label}</span>
        <span className="text-[10px] font-semibold tabular-nums text-fg-secondary opacity-40">{count}</span>
      </div>
      {children}
    </div>
  )
}

// ─── Delete confirm dialog ────────────────────────────────────────────────────

function DeleteConfirmDialog({
  layout,
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
}: {
  layout: Layout | null
  open: boolean
  onOpenChange: (v: boolean) => void
  onConfirm: () => void
  isDeleting: boolean
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded border border-line bg-canvas p-5 shadow-xl focus:outline-none"
          aria-describedby="delete-layout-desc"
        >
          <Dialog.Title className="text-sm font-semibold text-fg">
            Delete layout?
          </Dialog.Title>
          <Dialog.Description id="delete-layout-desc" className="mt-2 text-xs text-fg-secondary">
            <span className="font-semibold text-fg">{layout?.name}</span> will be permanently
            deleted. Pages already built from this layout will not be affected.
          </Dialog.Description>
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex items-center gap-1.5 rounded bg-red-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-50"
            >
              {isDeleting && <Loader2 size={11} className="animate-spin" aria-hidden="true" />}
              Delete
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

function LayoutListPage() {
  const navigate = useNavigate()
  const { data: layouts, isLoading, isError } = useLayouts()
  const { mutate: cloneLayout, isPending: isCloning, variables: cloningVars } = useCloneLayout()
  const { mutate: deleteLayout, isPending: isDeleting } = useDeleteLayout()

  const [deleteTarget, setDeleteTarget] = useState<Layout | null>(null)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const query = search.trim().toLowerCase()
  const allLayouts = layouts ?? []
  const officialLayouts = allLayouts.filter(
    (t) => t.usageContext.includes('official') && (!query || t.name.toLowerCase().includes(query))
  )
  const bookLayouts = allLayouts.filter(
    (t) => t.usageContext.includes('book') && (!query || t.name.toLowerCase().includes(query))
  )
  const selected = allLayouts.find((t) => t.id === selectedId) ?? null

  function handleClone(layout: Layout) {
    cloneLayout({ id: layout.id, name: `Copy of ${layout.name}` }, {
      onSuccess: (clone) => {
        toast.success('Layout cloned', { description: `"${clone.name}" created.` })
        navigate(`/admin/layouts/${clone.id}`)
      },
      onError: () => toast.error('Failed to clone layout.'),
    })
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return
    deleteLayout(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Layout deleted')
        if (selectedId === deleteTarget.id) setSelectedId(null)
        setDeleteTarget(null)
      },
      onError: () => toast.error('Failed to delete layout.'),
    })
  }

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col overflow-hidden">

      {/* Gradient wash */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-56"
        style={{ background: 'linear-gradient(to bottom, var(--violet-4), var(--violet-2), transparent)', opacity: 0.5 }}
      />

      {/* Header */}
      <div className="relative shrink-0 px-6 pt-5">
        <div className="flex items-center justify-between border-b border-line pb-4">
          <h1 className="font-zodiak pl-8 text-3xl font-bold text-fg">Layout Studio</h1>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-secondary" aria-hidden="true" />
              <input
                type="search"
                placeholder="Filter by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-48 rounded border border-line bg-canvas pl-7 pr-3 text-xs text-fg placeholder:text-fg-secondary focus:outline-none focus:ring-1 focus:ring-line-strong"
              />
            </div>
            <Link
              to="/admin/layouts/new"
              className="flex items-center gap-2 rounded border border-line bg-canvas px-4 py-2 text-xs font-semibold text-fg transition-colors hover:bg-fill"
            >
              <Plus size={13} aria-hidden="true" />
              New Layout
            </Link>
          </div>
        </div>
      </div>

      {/* Body: left rail + right panel */}
      <div className="relative flex flex-1 overflow-hidden">

        {/* ── Left rail ── */}
        <div className="flex w-64 shrink-0 flex-col overflow-hidden border-r border-line">

          {/* Stats */}
          {!isLoading && !isError && allLayouts.length > 0 && (
            <div className="shrink-0 border-b border-line px-4 py-2 text-[11px] text-fg-secondary">
              <span className="font-semibold text-fg">{allLayouts.length}</span> layouts
              {' · '}
              <span className="font-semibold text-fg">{officialLayouts.length}</span> official
              {' · '}
              <span className="font-semibold text-fg">{bookLayouts.length}</span> books
            </div>
          )}

          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto">

            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-10 text-fg-secondary">
                <Loader2 size={14} className="animate-spin" />
                <span className="sr-only">Loading layouts…</span>
              </div>
            )}

            {isError && (
              <p className="px-4 py-4 text-xs text-error-fg">
                Failed to load layouts. Please refresh.
              </p>
            )}

            {!isLoading && !isError && (
              <>
                {officialLayouts.length > 0 && (
                  <RailSection label="Official" count={officialLayouts.length}>
                    {officialLayouts.map((layout) => (
                      <LayoutRow
                        key={layout.id}
                        layout={layout}
                        selected={selectedId === layout.id}
                        onClick={() => setSelectedId(layout.id)}
                      />
                    ))}
                  </RailSection>
                )}

                {bookLayouts.length > 0 && (
                  <RailSection label="Books" count={bookLayouts.length}>
                    {bookLayouts.map((layout) => (
                      <LayoutRow
                        key={layout.id}
                        layout={layout}
                        selected={selectedId === layout.id}
                        onClick={() => setSelectedId(layout.id)}
                      />
                    ))}
                  </RailSection>
                )}

                {allLayouts.length === 0 && (
                  <div className="flex flex-col items-center px-4 py-12 text-center">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-line bg-fill">
                      <LayoutTemplate size={16} className="text-fg-secondary" aria-hidden="true" />
                    </div>
                    <p className="text-xs font-semibold text-fg-secondary">No layouts yet</p>
                    <Link
                      to="/admin/layouts/new"
                      className="mt-4 flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                    >
                      <Plus size={11} aria-hidden="true" />
                      New Layout
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Right panel ── */}
        {selected ? (
          <div className="flex flex-1 flex-col overflow-hidden">

            {/* Detail toolbar */}
            <div className="relative flex shrink-0 items-center justify-between border-b border-line bg-surface px-5 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-fg">{selected.name}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    {selected.usageContext.map((ctx) => (
                      <span
                        key={ctx}
                        className="rounded border border-line bg-fill px-1.5 py-0.5 text-[10px] font-semibold text-fg-secondary"
                      >
                        {ctx === 'official' ? 'Official' : 'Book'}
                      </span>
                    ))}
                  </div>
                </div>
                {selected.description && (
                  <p className="mt-0.5 text-xs text-fg-secondary">{selected.description}</p>
                )}
              </div>
              <div className="ml-4 flex shrink-0 items-center gap-1">
                <Link
                  to={`/admin/layouts/${selected.id}`}
                  className="flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                >
                  <ExternalLink size={12} aria-hidden="true" />
                  Open in Editor
                </Link>
                <button
                  type="button"
                  onClick={() => handleClone(selected)}
                  disabled={isCloning && cloningVars?.id === selected.id}
                  className="flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50"
                >
                  {isCloning && cloningVars?.id === selected.id
                    ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                    : <Copy size={12} aria-hidden="true" />
                  }
                  Clone
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(selected)}
                  aria-label={`Delete ${selected.name}`}
                  className="flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-error-fg"
                >
                  <Trash2 size={12} aria-hidden="true" />
                  Delete
                </button>
              </div>
            </div>

            {/* Full-size preview */}
            <div className="flex-1 overflow-y-auto">
              <LayoutPreview slots={selected.blocks as CanvasSlot[]} mode="inline" />
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <LayoutTemplate size={36} className="mx-auto mb-3 text-fg-secondary opacity-20" aria-hidden="true" />
              <p className="text-sm text-fg-secondary">Select a layout to preview</p>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmDialog
        layout={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null) }}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default LayoutListPage
